import React, { useMemo } from 'react';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogActionButton from '../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import { useFormik } from 'formik';
import { usersApiService } from '../../../graphql/users/usersApiService';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import { TeamConnectionGq } from '../../../graphql/users/models/TeamConnectionGq';
import { chain } from 'lodash';
import moment from 'moment';
import { useLicenseIncreaseWarning } from '../SubscriptionPage/useLicenseIncreaseWarning';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import LicenseIncreaseWarningDialog from '../SubscriptionPage/LicenseIncreaseWarningDialog';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';

export interface NewConnectionDialogRequestAccessProps {
  close: () => void;
  back: () => void;
  onSuccess: () => Promise<unknown>;
  connections: TeamConnectionGq[];
  team: CurrentTeamGq;
  minWidth: string;
}

export const NewConnectionDialogRequestAccess: React.FC<NewConnectionDialogRequestAccessProps> = ({
  close,
  onSuccess,
  back,
  connections,
  team,
  minWidth,
}) => {
  const mutation = usersApiService.mutations.useCreateConnectionInvitation();
  const licenseWarningBag = useLicenseIncreaseWarning(team);
  const formikProps = useFormik({
    initialValues: {
      email: '',
    },
    validate: (values) => {
      if (values.email.includes('@') && values.email.length < 3) {
        return { email: 'Provide a valid email' };
      }
      return undefined;
    },
    onSubmit: (values) =>
      licenseWarningBag.handleAttemptToAddChildTeams(() =>
        mutation({ sendToEmail: values.email, invitedTeamType: 'receive' }).then(onSuccess)
      ),
  });

  const spamCount = useMemo(
    () =>
      chain(connections)
        .flatMap((c) => c.invitations)
        .filter((c) => !!c.sentTime && moment.utc(c.sentTime).isAfter(moment.utc().add(-1, 'days')))
        .value().length,
    [connections]
  );
  const preventSpam = spamCount > 30;

  if (licenseWarningBag.showWarning) {
    return <LicenseIncreaseWarningDialog licenseWarningBag={licenseWarningBag} />;
  }

  return (
    <VoxelDialog open title="Request other team's data" onClose={close} closeOnBackdrop>
      <VoxelDialogContent classMinWidth={minWidth}>
        <VoxelText paddingBot paddingTop>
          Who would you like to collaborate with? No worries if they don't have Voxel yet, we'll set
          them up.
        </VoxelText>
        <div className="mt-4">
          <VoxelInput
            labelPosition="top"
            label="Collaborator's email address"
            placeholder="john.doe@gmail.com"
            value={formikProps.values.email}
            onChange={(event) => formikProps.setFieldValue('email', event.target.value)}
          />
        </div>
      </VoxelDialogContent>
      {preventSpam && (
        <VoxelDialogContent>
          <VoxelAlert size="sm">
            Functionality temporarily disabled. Contact support for help.
          </VoxelAlert>
        </VoxelDialogContent>
      )}
      <VoxelDialogActions>
        <VoxelDialogActionButton title="Back" onClick={back} />
        <VoxelDialogActionSubmit
          title="Send connection invitation"
          onClick={formikProps.submitForm}
          disabled={formikProps.isSubmitting || !formikProps.isValid || preventSpam}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default NewConnectionDialogRequestAccess;
